package fyi.moko.hotpl.data.image

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Process-lifetime LRU of friends' shared photo bytes (`shared/{ownerUid}/{fileName}`),
 * bounded by total bytes rather than entry count because covers range from 30 KB to the
 * 5 MB rules cap.
 *
 * Memory only, on purpose: a friend's bytes must never land in my own photo store where
 * they would look like mine (see `rememberSharedPhoto`), but re-downloading every thumbnail
 * on every recomposition of the Friends list was pure waste. Invalidation is unnecessary —
 * a shared file name is random and immutable, so a changed photo arrives as a new name via
 * the projection, and the old entry just ages out.
 */
class SharedPhotoMemoryCache(private val maxBytes: Long = DEFAULT_MAX_BYTES) {
    private val mutex = Mutex()
    // Access-ordered by hand: LinkedHashMap's accessOrder flag isn't in the common stdlib.
    private val entries = LinkedHashMap<String, ByteArray>()
    private var size = 0L

    val currentBytes: Long get() = size

    suspend fun get(ownerUid: String, fileName: String): ByteArray? = mutex.withLock {
        val key = key(ownerUid, fileName)
        entries.remove(key)?.also { entries[key] = it }
    }

    suspend fun put(ownerUid: String, fileName: String, bytes: ByteArray) {
        if (bytes.size > maxBytes) return
        mutex.withLock {
            val key = key(ownerUid, fileName)
            entries.remove(key)?.let { size -= it.size }
            entries[key] = bytes
            size += bytes.size
            val oldest = entries.entries.iterator()
            while (size > maxBytes && oldest.hasNext()) {
                val victim = oldest.next()
                size -= victim.value.size
                oldest.remove()
            }
        }
    }

    private fun key(ownerUid: String, fileName: String) = "$ownerUid/$fileName"

    companion object {
        /** Roughly 40 typical covers, or a handful of full-size ones. */
        const val DEFAULT_MAX_BYTES: Long = 8L * 1024 * 1024
    }
}
